import styled from 'styled-components';
import { Typography } from '@mui/material';
import { Chip, Flex, FlexColumn } from '@elementary/common-ui/components';
import { pluralizeString } from '@elementary/common-ui/utils/common';

export const ChangesList = styled(FlexColumn)`
  border-radius: 8px;
  border: ${({ theme }) => `1px solid ${theme.palette.common.gray}`};
  background-color: ${({ theme }) => theme.palette.common.gray3};
`;

export const Item = styled(FlexColumn)`
  padding: 14px;

  &:not(:last-of-type) {
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.common.gray}`};
  }
`;

export const ChangeRow = styled(Flex)`
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

export const ImpactRow = styled(Flex)`
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

export const SubText = styled(Typography)`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const CHANGE_TYPE_LABELS: Record<string, string> = {
  column_added: 'Added',
  column_removed: 'Removed',
  column_type_changed: 'Type changed',
  nullable_changed: 'Nullability changed',
};

const CHANGE_TYPE_COLORS: Record<string, 'success' | 'error' | 'warning'> = {
  column_added: 'success',
  column_removed: 'error',
  column_type_changed: 'warning',
  nullable_changed: 'warning',
};

type Props = {
  columnName: string;
  changeType: string;
  dataType: string | null;
  previousDataType: string | null;
  isNullable?: boolean | null;
  previousIsNullable?: boolean | null;
  downstreamTableAssets: number;
  downstreamBiAssets: number;
  totalDownstreamColumns: number;
  hasCriticalDownstream: boolean;
};

const ColumnChangeItem = ({
  columnName,
  changeType,
  dataType,
  previousDataType,
  isNullable,
  previousIsNullable,
  downstreamTableAssets,
  downstreamBiAssets,
  totalDownstreamColumns,
  hasCriticalDownstream,
}: Props) => (
  <Item>
    <ChangeRow>
      <FlexColumn sx={{ gap: 0.5 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
          {columnName}
        </Typography>
        {changeType === 'column_type_changed' && (
          <SubText>
            {previousDataType} → {dataType}
          </SubText>
        )}
        {changeType === 'column_added' && dataType && (
          <SubText>Type: {dataType}</SubText>
        )}
        {changeType === 'nullable_changed' && (
          <SubText>
            {previousIsNullable == null
              ? 'Unknown'
              : previousIsNullable
              ? 'Nullable'
              : 'Not nullable'}{' '}
            →{' '}
            {isNullable == null
              ? 'Unknown'
              : isNullable
              ? 'Nullable'
              : 'Not nullable'}
          </SubText>
        )}
      </FlexColumn>
      <Flex sx={{ gap: 1, alignItems: 'center' }}>
        {hasCriticalDownstream && (
          <Chip label="Critical" color="error" size="small" />
        )}
        <Chip
          label={CHANGE_TYPE_LABELS[changeType] || changeType}
          color={CHANGE_TYPE_COLORS[changeType] || 'default'}
          size="small"
        />
      </Flex>
    </ChangeRow>
    {(downstreamTableAssets > 0 ||
      downstreamBiAssets > 0 ||
      totalDownstreamColumns > 0) && (
      <ImpactRow>
        {downstreamTableAssets > 0 && (
          <SubText>
            {pluralizeString(
              downstreamTableAssets,
              'table asset',
              'table assets',
            )}
          </SubText>
        )}
        {downstreamBiAssets > 0 && (
          <SubText>
            {pluralizeString(downstreamBiAssets, 'BI asset', 'BI assets')}
          </SubText>
        )}
        {totalDownstreamColumns > 0 && (
          <SubText>
            {pluralizeString(
              totalDownstreamColumns,
              'downstream column',
              'downstream columns',
            )}
          </SubText>
        )}
      </ImpactRow>
    )}
  </Item>
);

export default ColumnChangeItem;
